import "@/App.css";
import { motion } from "framer-motion";
import {
  Activity,
  Droplets,
  ScanLine,
  Brain,
  Pill,
  LineChart,
  Heart,
  ShieldAlert,
  ShieldCheck,
  ArrowRight,
  ChevronDown,
  Globe,
  Sparkles,
  Gift,
} from "lucide-react";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { LanguageProvider, useLanguage } from "./hooks/useLanguage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import WaitlistAdmin from "./pages/WaitlistAdmin";
import BetaAdmin from "./pages/BetaAdmin";
import DeveloperAudit from "./pages/DeveloperAudit";
import DeveloperAuditIntake from "./pages/DeveloperAuditIntake";
import SampleReport from "./pages/SampleReport";
import BetaSignupForm from "./components/BetaSignupForm";
import { FloatingPhone } from "./components/PhoneMockup";
import AnimatedGradientOrbs from "./components/AnimatedGradientOrbs";

const track = (event, props = {}) => {
  try {
    if (typeof window !== "undefined" && window.posthog) {
      window.posthog.capture(event, props);
    }
  } catch (_e) {
    /* silent */
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const NephrixLogo = ({ className = "h-9 w-auto" }) => (
  <img src="/nephrix-logo.png" alt="Nephrix" className={className} />
);

// Language switcher (site-wide language, dark theme)
const LanguageSwitcher = () => {
  const { currentLang, switchLanguage, languages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const current = languages.find((l) => l.code === currentLang);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-2 rounded-full bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700 transition-colors text-sm font-medium text-slate-200"
        data-testid="language-switcher"
        aria-label="Change language"
      >
        <Globe className="w-4 h-4" />
        <span>{current?.flag} {current?.code.toUpperCase()}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 py-2 w-44 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => { switchLanguage(lang.code); setIsOpen(false); }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-800 flex items-center gap-2 ${
                  currentLang === lang.code ? "text-teal-300 font-semibold" : "text-slate-300"
                }`}
                data-testid={`lang-${lang.code}`}
              >
                <span>{lang.flag}</span>
                <span>{lang.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// ============ SECTIONS ============

const Header = () => {
  const { t } = useLanguage();
  return (
    <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-950/80 backdrop-blur-lg">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <NephrixLogo className="h-10 w-auto" />
          <span className="text-slate-100 font-semibold text-lg tracking-tight hidden sm:block" style={{ fontFamily: "Manrope, sans-serif" }}>
            Nephrix
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          <button onClick={() => scrollToId("features")} className="text-sm text-slate-300 hover:text-teal-300 transition-colors" data-testid="nav-features">
            {t("nav_features")}
          </button>
          <button onClick={() => scrollToId("story")} className="text-sm text-slate-300 hover:text-teal-300 transition-colors" data-testid="nav-story">
            {t("nav_story")}
          </button>
          <button onClick={() => scrollToId("beta")} className="text-sm text-slate-300 hover:text-teal-300 transition-colors" data-testid="nav-beta">
            {t("nav_beta")}
          </button>
        </nav>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <button
            onClick={() => scrollToId("beta")}
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold text-sm transition-colors"
            data-testid="header-cta"
          >
            {t("header_cta")}
          </button>
        </div>
      </div>
    </header>
  );
};

const Hero = () => {
  const { t } = useLanguage();
  return (
    <section className="relative overflow-hidden pt-16 pb-24 md:pt-24 md:pb-36">
      <AnimatedGradientOrbs />
      {/* subtle grid overlay */}
      <div
        className="absolute inset-0 -z-10 opacity-[0.035]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(148,163,184,1) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,1) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage:
            "radial-gradient(ellipse 80% 60% at 50% 30%, black 0%, transparent 75%)",
        }}
      />

      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center">
          {/* LEFT: copy */}
          <div className="text-center lg:text-left">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-400/20 text-teal-300 text-xs font-semibold tracking-widest uppercase"
            >
              <Sparkles className="w-3.5 h-3.5" /> {t("hero_badge")}
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 text-4xl sm:text-5xl lg:text-[64px] font-bold text-slate-50 tracking-tight leading-[1.02]"
              style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.02em" }}
              data-testid="hero-headline"
            >
              {t("hero_headline")}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 max-w-xl lg:max-w-lg mx-auto lg:mx-0 text-lg md:text-xl text-slate-300 leading-relaxed"
            >
              {t("hero_sub")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-10 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4"
            >
              <button
                onClick={() => scrollToId("beta")}
                className="group inline-flex items-center gap-2 px-7 py-4 bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold rounded-full transition-all shadow-[0_10px_40px_-10px_rgba(20,184,166,0.6)] hover:shadow-[0_20px_50px_-10px_rgba(20,184,166,0.8)] hover:-translate-y-0.5"
                data-testid="hero-cta-primary"
              >
                {t("hero_cta_primary")}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <button
                onClick={() => scrollToId("features")}
                className="inline-flex items-center gap-2 px-7 py-4 bg-slate-800/60 hover:bg-slate-800 border border-slate-700 text-slate-100 font-semibold rounded-full transition-colors backdrop-blur"
                data-testid="hero-cta-secondary"
              >
                {t("hero_cta_secondary")}
              </button>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-2.5"
            >
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900/60 border border-slate-700/60 text-xs text-slate-300">
                <Gift className="w-3.5 h-3.5 text-teal-300" />
                {t("trust_free")}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900/60 border border-slate-700/60 text-xs text-slate-300">
                <Heart className="w-3.5 h-3.5 text-teal-300" />
                {t("trust_patient")}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900/60 border border-slate-700/60 text-xs text-slate-300">
                <ShieldCheck className="w-3.5 h-3.5 text-teal-300" />
                {t("trust_privacy")}
              </span>
            </motion.div>
          </div>

          {/* RIGHT: iPhone mockup */}
          <div className="relative flex items-center justify-center lg:justify-end">
            {/* Glow behind phone */}
            <div
              aria-hidden
              className="absolute inset-0 -z-10"
              style={{
                background:
                  "radial-gradient(ellipse 60% 60% at 60% 50%, rgba(20,184,166,0.20), transparent 70%)",
                filter: "blur(30px)",
              }}
            />
            <FloatingPhone src="/app-home.jpeg" alt="Nephrix app dashboard" />
          </div>
        </div>
      </div>
    </section>
  );
};

const Problem = () => {
  const { t } = useLanguage();
  return (
    <section className="relative py-28 md:py-40 bg-slate-900/30 border-y border-slate-800/60 overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-40"
        style={{
          background:
            "radial-gradient(ellipse 40% 50% at 20% 50%, rgba(37,99,235,0.10), transparent 70%), radial-gradient(ellipse 40% 50% at 80% 50%, rgba(20,184,166,0.08), transparent 70%)",
        }}
      />
      <div className="max-w-3xl mx-auto px-6 text-center">
        <motion.h2
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="text-4xl md:text-6xl font-bold text-slate-50 tracking-tight leading-[1.05]"
          style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.02em" }}
          data-testid="problem-title"
        >
          {t("problem_title")}
        </motion.h2>
        <motion.p
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          transition={{ delay: 0.15 }}
          className="mt-8 text-lg md:text-xl text-slate-300 leading-relaxed"
        >
          {t("problem_body")}
        </motion.p>
      </div>
    </section>
  );
};

const FEATURE_ICONS = {
  tracking: Activity,
  fluid: Droplets,
  lab: ScanLine,
  copilot: Brain,
  meds: Pill,
  timeline: LineChart,
};

const Features = () => {
  const { t } = useLanguage();
  const items = ["tracking", "fluid", "lab", "copilot", "meds", "timeline"];
  return (
    <section id="features" className="relative py-28 md:py-40">
      {/* subtle backdrop tint */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 50% 40% at 50% 0%, rgba(20,184,166,0.06), transparent 70%)",
        }}
      />

      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-20"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-400/20 text-blue-300 text-xs font-semibold tracking-widest uppercase">
            {t("features_badge")}
          </span>
          <h2
            className="mt-6 text-4xl md:text-6xl font-bold text-slate-50 tracking-tight leading-[1.05]"
            style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.02em" }}
          >
            {t("features_title")}
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {items.map((key) => {
            const Icon = FEATURE_ICONS[key];
            return (
              <motion.div
                key={key}
                variants={fadeInUp}
                whileHover={{ y: -4 }}
                transition={{ type: "spring", stiffness: 300, damping: 24 }}
                className="group relative bg-slate-900/60 border border-slate-800 rounded-3xl p-8 md:p-9 min-h-[240px] hover:border-teal-400/40 hover:bg-slate-900/80 transition-colors backdrop-blur-sm overflow-hidden"
                data-testid={`feature-${key}`}
              >
                {/* Corner glow on hover */}
                <div
                  aria-hidden
                  className="absolute -top-20 -right-20 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(20,184,166,0.25) 0%, transparent 70%)",
                    filter: "blur(20px)",
                  }}
                />
                <div className="relative">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-400/20 to-blue-500/20 border border-teal-400/20 flex items-center justify-center mb-6 group-hover:from-teal-400/30 group-hover:to-blue-500/30 group-hover:scale-105 transition-all">
                    <Icon className="w-7 h-7 text-teal-300" strokeWidth={1.5} />
                  </div>
                  <h3
                    className="text-xl md:text-2xl font-bold text-slate-50 mb-3 tracking-tight"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    {t(`feature_${key}_title`)}
                  </h3>
                  <p className="text-[15px] text-slate-400 leading-relaxed">
                    {t(`feature_${key}_desc`)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

const FounderStory = () => {
  const { t } = useLanguage();
  return (
    <section id="story" className="relative py-28 md:py-40 bg-slate-900/30 border-y border-slate-800/60 overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 50% 50% at 50% 50%, rgba(20,184,166,0.08), transparent 70%)",
        }}
      />
      <div className="max-w-3xl mx-auto px-6 text-center">
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-400/20 text-teal-300 text-xs font-semibold tracking-widest uppercase mb-6"
        >
          <Heart className="w-3.5 h-3.5" /> {t("story_badge")}
        </motion.div>
        <motion.h2
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-4xl md:text-6xl font-bold text-slate-50 tracking-tight leading-[1.05]"
          style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.02em" }}
        >
          {t("story_title")}
        </motion.h2>
        <motion.p
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="mt-8 text-lg md:text-xl text-slate-300 leading-relaxed"
        >
          {t("story_body")}
        </motion.p>
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mt-12 inline-flex items-center gap-3 px-6 py-3 rounded-full bg-slate-900 border border-teal-400/30 shadow-[0_0_40px_-10px_rgba(20,184,166,0.35)]"
        >
          <Heart className="w-4 h-4 text-teal-400" />
          <span className="text-sm md:text-base text-teal-200 font-medium italic">{t("story_tagline")}</span>
        </motion.div>
      </div>
    </section>
  );
};

const MedicalSafety = () => {
  const { t } = useLanguage();
  return (
    <section className="py-16 md:py-20">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6 md:p-8 flex items-start gap-4"
          data-testid="medical-safety"
        >
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-amber-200 uppercase tracking-widest mb-2">{t("safety_title")}</h3>
            <p className="text-sm md:text-base text-slate-300 leading-relaxed">
              {t("safety_body")}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer className="border-t border-slate-800 bg-slate-950">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <NephrixLogo className="h-9 w-auto" />
              <span className="text-slate-100 font-semibold text-lg tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
                Nephrix
              </span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-sm">
              {t("footer_tagline")}
            </p>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-widest mb-4">
              {t("footer_legal")}
            </h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/privacy" className="text-slate-400 hover:text-teal-300 transition-colors">{t("privacy_policy")}</Link></li>
              <li><Link to="/terms" className="text-slate-400 hover:text-teal-300 transition-colors">{t("terms_of_use")}</Link></li>
              <li><a href="#safety" onClick={(e) => { e.preventDefault(); scrollToId("safety"); }} className="text-slate-400 hover:text-teal-300 transition-colors">{t("medical_disclaimer")}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-widest mb-4">
              {t("footer_help")}
            </h4>
            <ul className="space-y-2 text-sm">
              <li><a href="mailto:support@nephrix.health" className="text-slate-400 hover:text-teal-300 transition-colors">{t("footer_support")}</a></li>
              <li><a href="mailto:support@nephrix.health" className="text-slate-400 hover:text-teal-300 transition-colors">{t("footer_contact")}</a></li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-xs text-slate-500">© 2026 Nephrix Health Solutions. {t("all_rights_reserved")}</p>
          <p className="text-xs text-slate-500">support@nephrix.health</p>
        </div>
      </div>
    </footer>
  );
};

const LandingPage = () => {
  useEffect(() => {
    track("landing_page_view", { path: window.location.pathname });
  }, []);
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Header />
      <main>
        <Hero />
        <Problem />
        <Features />
        <FounderStory />
        <BetaSignupForm />
        <div id="safety">
          <MedicalSafety />
        </div>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/admin/waitlist" element={<WaitlistAdmin />} />
          <Route path="/admin/beta" element={<BetaAdmin />} />
          <Route path="/developer-audit" element={<DeveloperAudit />} />
          <Route path="/developer-audit/index.html" element={<DeveloperAudit />} />
          <Route path="/developer-audit/intake" element={<DeveloperAuditIntake />} />
          <Route path="/developer-audit/sample-report" element={<SampleReport />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
